
import {
  defineComponent,
  computed,
} from '@vue/composition-api';
import { LinkButton } from '@fc/angie-ui';
import MemberStoryCard from '~/components/v5/home/MemberStoryCard.vue';
import MemberQuoteCard from '~/components/v5/home/MemberQuoteCard.vue';
import FCSwiper from '~/components/FCSwiper.vue';
import { isMobileEffect } from '~/effects/matchMedia';

interface MemberStory {
  id: number;
  memberName: string;
  description: string;
  imageUrl?: string;
  imageAlt?: string;
  quote?: string;
  description2?: string;
  videoUrl?: string;
  mobileVideoUrl?: string;
  bgColor?: string;
}

export default defineComponent({
  name: 'MemberStoriesSection',
  components: {
    LinkButton,
    MemberStoryCard,
    MemberQuoteCard,
    FCSwiper,
  },
  setup() {
    const isMobile = isMobileEffect();

    const memberStories: {
      column1: MemberStory[];
      column2: MemberStory[];
      column3: MemberStory[];
      column4: MemberStory[];
    } = {
      column1: [
        {
          id: 1,
          imageUrl: 'https://a.storyblok.com/f/152150/1000x1500/b841af474b/ashley_portrait-1.jpg',
          imageAlt: 'A woman wearing boxing gloves throws a punch, focused and determined, with a blurred background and decorative fringe above.',
          memberName: 'Ashley',
          description: 'Member since March 2024',
          videoUrl: 'https://cdn-b.joinfightcamp.com/videos/Homepage_Section2Video_Ashley_001.mp4',
          mobileVideoUrl: 'https://cdn-b.joinfightcamp.com/videos/Homepage_Section2Video_9x16_Ashley_001.mp4',
        },
        {
          id: 2,
          imageUrl: 'https://a.storyblok.com/f/152150/6000x4000/a6427f557a/thomas_stills-52.jpg',
          imageAlt: 'FightCamp member Thomas',
          memberName: 'Thomas',
          description: 'Ex-NFL player.',
          description2: 'Member since May 2024',
          quote: 'I\'m in better shape now as a Joe, than I was as a Pro, and I really believe so.',
        },
      ],
      column2: [
        {
          id: 3,
          quote: 'Being able to combine strength with fighting workouts make this hard to beat. Also learn a ton which keeps me engaged. Been using this for 2 years now and see no end in my future.',
          memberName: 'James',
          bgColor: 'blue',
          description: 'Member since October 2023',
        },
        {
          id: 4,
          imageUrl: 'https://a.storyblok.com/f/152150/5515x7353/3eb9117534/jared_portrait-1.jpg',
          imageAlt: 'Man in a "Fight Camp" tank top holds boxing gloves, standing next to FightCamp punching bag in a home gym.',
          memberName: 'Jared',
          description: 'Member since June 2024',
          videoUrl: 'https://cdn-b.joinfightcamp.com/videos/Homepage_Section2Video_Jared_001.mp4',
          mobileVideoUrl: 'https://cdn-b.joinfightcamp.com/videos/Homepage_Section2Video_9x16_Jared_001.mp4',
        },
        {
          id: 5,
          quote: 'I purchased the base system on sale and wasn\'t sure I would like it. I am now a serious fan. I am 56 and a pretty serious runner and cyclist (V02 Max of 62). Have been doing weight training pretty seriously for the last year also. The FightCamp system is legit. The workouts are as educational as they are physically challenging. Highly recommend.',
          memberName: 'L.J. Wilson',
          bgColor: 'red',
          description: 'Member since November 2024',
        },
      ],
      column3: [
        {
          id: 6,
          imageUrl: 'https://a.storyblok.com/f/152150/5857x3905/2dea68f03b/monica_stills-159.jpg',
          imageAlt: 'FightCamp member training',
          memberName: 'Monica',
          quote: 'Hitting the bag goes by fast, it’s entertaining, it’s fun, it feels different, it challenged me in new ways, that for me was a breath of fresh air.',
          description: 'Certified Trainer.',
          description2: 'Member since August 2025',
        },
        {
          id: 7,
          imageUrl: 'https://a.storyblok.com/f/152150/6192x8256/8d1e4dbe63/josh_portrait.jpg',
          imageAlt: 'Man in a sleeveless shirt flexing his arms in a room with a FightCamp punching bag, motivational posters, and string lights.',
          memberName: 'Josh',
          description: 'Member since January 2021',
          videoUrl: 'https://cdn-b.joinfightcamp.com/videos/Homepage_Section2Video_Josh_001.mp4',
          mobileVideoUrl: 'https://cdn-b.joinfightcamp.com/videos/Homepage_Section2Video_9x16_Josh_001.mp4',
        },
      ],
      column4: [
        {
          id: 8,
          quote: 'I\'ve bought a lot of different gym equipment. It sits there and collects dust. Don\'t pass this up. It is a lot of fun with the trackers. I\'ve lost 40 lbs and I got a mean hook now 6 months into it.',
          memberName: 'Matt',
          bgColor: 'red',
          description: 'Member since April 2025',
        },
        {
          id: 9,
          imageUrl: 'https://a.storyblok.com/f/152150/4000x6000/19e4bbef21/kellie_portrait-1.jpg',
          imageAlt: 'Person wearing boxing gloves and a black T-shirt stands in a room with wooden beams and a couch, smiling at the camera.',
          memberName: 'Kellie',
          quote: 'Fightcamp pretty much changed my life, I don’t say that lightly. I’m that 36yr old mom, I do Fightcamp in my basement, and as a mom I can’t always have the time to do a workout, now Fightcamp is my vibe.',
          description: 'Member since September 2023',
        },
        {
          id: 10,
          quote: 'I hate cardio....always have. I have never done upper and full body cardio like this before. Muscles I never felt before were screaming. I was instantly addicted. Can\'t wait to try other areas like kickboxing and Muay Thai as well.',
          memberName: 'Andrew',
          bgColor: 'blue',
          description: 'Member since March 2025',
        },
      ],
    };

    // Flatten all stories into a single array for mobile carousel
    const allStories = computed(() => [
      ...memberStories.column1,
      ...memberStories.column2,
      ...memberStories.column3,
      ...memberStories.column4,
    ]);

    return {
      isMobile,
      memberStories,
      allStories,
    };
  },
});
